import { useQuery } from "@tanstack/react-query";
import { Download, TrendingUp } from "lucide-react";
import { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  descargarPnlContableXlsx,
  getPnlContable,
  type PnlEmitida,
  type PnlMes,
  type PnlRecibida,
} from "@/lib/api";

/**
 * Reportes → P&L Contable.
 *
 * Estado de resultados que une las facturas del ETRM con los estados de cuenta
 * de CENACE. La base no es obvia y está conciliada contra la hoja de la
 * dirección: ingresos por FECHA LÍMITE DE PAGO, CENACE por FECHA DE PAGO, todo
 * sin IVA y los dólares a un tipo de cambio único. El backend hace el cálculo;
 * aquí sólo se pinta.
 */

const COLOR_ING = "#f59e0b"; // ámbar
const COLOR_COS = "#4f46e5"; // índigo
const COLOR_UTIL = "#059669"; // esmeralda

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun",
               "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const nf0 = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const money0 = (n: number) => (n < 0 ? "−" : "") + "$" + nf0.format(Math.abs(n));
const money2 = (n: number) => (n < 0 ? "−" : "") + nf2.format(Math.abs(n));

/** '2026-10' → 'Oct 26' */
const etiquetaMes = (mes: string) => `${MESES[+mes.slice(5, 7) - 1]} ${mes.slice(2, 4)}`;

/** Abrevia para los ejes: 12,345,678 → '12 M' */
function compacto(x: number): string {
  const a = Math.abs(x);
  if (a >= 1e6) return `${(x / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(".0", "")} M`;
  if (a >= 1e3) return `${(x / 1e3).toFixed(0)} k`;
  return String(Math.round(x));
}

type Campo = keyof PnlMes;

/** Renglones del estado, en orden. `campo` es el de la base sin IVA; con IVA se
 *  le pega el sufijo. */
const RENGLONES: { tipo: "banda" | "linea" | "total" | "utilidad"; etiqueta: string; campo?: Campo }[] = [
  { tipo: "banda", etiqueta: "Ingresos" },
  { tipo: "linea", etiqueta: "EE + Potencia", campo: "ing_ee" },
  { tipo: "linea", etiqueta: "CEL", campo: "ing_cel" },
  { tipo: "linea", etiqueta: "Consultorías", campo: "ing_cons" },
  { tipo: "linea", etiqueta: "Ingresos CENACE", campo: "ing_cenace" },
  { tipo: "linea", etiqueta: "Proyección B2B", campo: "ing_proy" },
  { tipo: "total", etiqueta: "TOTAL INGRESOS", campo: "total_ing" },
  { tipo: "banda", etiqueta: "Costos" },
  { tipo: "linea", etiqueta: "EE + Potencia", campo: "cos_ee" },
  { tipo: "linea", etiqueta: "CEL", campo: "cos_cel" },
  { tipo: "linea", etiqueta: "Servicios y consultorías", campo: "cos_ser" },
  { tipo: "linea", etiqueta: "Costos CENACE", campo: "cos_cenace" },
  { tipo: "linea", etiqueta: "Proyección B2B", campo: "cos_proy" },
  { tipo: "total", etiqueta: "TOTAL COSTOS", campo: "total_cos" },
  { tipo: "utilidad", etiqueta: "UTILIDAD", campo: "utilidad" },
];

const LINEA_LABEL: Record<string, string> = {
  ee_potencia: "EE + Potencia",
  cel: "CEL",
  servicios: "Servicios y consultorías",
};

export function PnlContablePage() {
  const [conIva, setConIva] = useState(false);
  // Tipo de cambio único para TODO el estado (no el de cada comprobante): así
  // está conciliada la hoja de la dirección.
  const [tcTexto, setTcTexto] = useState("17.50");
  const [tcAplicado, setTcAplicado] = useState(17.5);
  const [detalle, setDetalle] = useState<"ninguno" | "emitidas" | "recibidas">("ninguno");
  const [bajando, setBajando] = useState(false);
  const [errorBaja, setErrorBaja] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["pnl-contable", tcAplicado],
    queryFn: () => getPnlContable(tcAplicado),
    staleTime: 5 * 60_000,
  });

  const suf = conIva ? "_iva" : "";
  const val = (m: PnlMes, campo: Campo): number => (m[`${campo}${suf}` as Campo] as number) ?? 0;

  const meses = useMemo(() => q.data?.meses ?? [], [q.data]);
  const mesEnCurso = q.data?.meta.mes_en_curso ?? "";

  /** Un mes es "parcial" si no tiene estados de cuenta de CENACE, si es el mes
   *  en curso, o si ya sólo trae proyección. No es comparable con los demás. */
  const motivoParcial = (m: PnlMes): string | null => {
    if (m.mes > mesEnCurso) return "proyección";
    if (m.mes === mesEnCurso) return "en curso";
    if (m.fechas_cenace === 0) return "sin CENACE";
    return null;
  };

  const datosGrafica = useMemo(
    () =>
      meses.map((m) => ({
        mes: etiquetaMes(m.mes),
        parcial: Boolean(motivoParcial(m)),
        ingresos: val(m, "total_ing"),
        costos: val(m, "total_cos"),
        utilidad: val(m, "utilidad"),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meses, suf, mesEnCurso],
  );

  const totales = useMemo(() => {
    const ing = meses.reduce((s, m) => s + val(m, "total_ing"), 0);
    const cos = meses.reduce((s, m) => s + val(m, "total_cos"), 0);
    const completos = meses.filter((m) => !motivoParcial(m));
    const ingC = completos.reduce((s, m) => s + val(m, "total_ing"), 0);
    const cosC = completos.reduce((s, m) => s + val(m, "total_cos"), 0);
    return {
      ing,
      cos,
      utilidad: ing - cos,
      completos,
      margen: ingC ? ((ingC - cosC) / ingC) * 100 : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meses, suf, mesEnCurso]);

  const bajar = async () => {
    setBajando(true);
    setErrorBaja(null);
    try {
      await descargarPnlContableXlsx(tcAplicado, conIva);
    } catch (e) {
      setErrorBaja((e as Error).message);
    } finally {
      setBajando(false);
    }
  };

  const aplicarTc = () => {
    const n = Number(tcTexto.replace(",", "."));
    if (Number.isFinite(n) && n > 0) setTcAplicado(n);
    else setTcTexto(tcAplicado.toFixed(2));
  };

  const meta = q.data?.meta;
  const rango = meses.length
    ? `${etiquetaMes(meses[0].mes)} – ${etiquetaMes(meses[meses.length - 1].mes)}`
    : "";

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-slate-400" />
          P&amp;L Contable
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-4xl">
          Estado de resultados de gestión: facturas emitidas y recibidas del ETRM más los
          estados de cuenta de CENACE. Los ingresos propios van por el mes de su{" "}
          <b>fecha límite de pago</b>, las líneas de CENACE por su <b>fecha de pago</b>, y
          de los meses que aún no facturan entra la <b>proyección B2B</b>. Pesos mexicanos.
        </p>
      </div>

      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(q.error as Error)?.message}
        </div>
      )}
      {errorBaja && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          No se pudo bajar el Excel: {errorBaja}
        </div>
      )}

      {/* Controles */}
      <section className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-x-5 gap-y-3">
        <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5">
          {[
            { v: false, label: "Sin IVA (P&L)" },
            { v: true, label: "Con IVA (facturado)" },
          ].map((o) => (
            <button
              key={String(o.v)}
              type="button"
              aria-pressed={conIva === o.v}
              onClick={() => setConIva(o.v)}
              className={`px-3 py-1.5 text-sm rounded ${
                conIva === o.v ? "bg-white shadow-sm text-slate-900 font-medium" : "text-slate-500"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600">
          Tipo de cambio único
          <input
            value={tcTexto}
            onChange={(e) => setTcTexto(e.target.value)}
            onBlur={aplicarTc}
            onKeyDown={(e) => e.key === "Enter" && aplicarTc()}
            inputMode="decimal"
            className="w-24 rounded border border-slate-300 px-2 py-1 text-sm text-right tabular-nums"
            title="USD→MXN aplicado a TODO el estado, no el tipo de cambio de cada comprobante"
          />
          <span className="text-slate-400">USD→MXN</span>
        </label>

        <button
          type="button"
          onClick={bajar}
          disabled={bajando || !q.data}
          className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {bajando ? "Generando…" : "Excel"}
        </button>

        {meta && (
          <span className="text-xs text-slate-400 ml-auto">
            {meta.n_emitidas} emitidas · {meta.n_recibidas} recibidas · {meta.n_servicios} de
            servicios · {meta.n_proyeccion} renglones de proyección
            {meta.cenace_hasta && ` · CENACE hasta ${meta.cenace_hasta}`}
          </span>
        )}
      </section>

      {q.isLoading && <div className="text-sm text-slate-500">Cargando el estado…</div>}

      {q.data && meses.length > 0 && (
        <>
          {/* KPIs */}
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tarjeta titulo={`Ingresos ${rango}`} valor={money0(totales.ing)} color="text-amber-700"
                     nota="incluye meses incompletos" />
            <Tarjeta titulo={`Costos ${rango}`} valor={money0(totales.cos)} color="text-indigo-700"
                     nota="incluye meses incompletos" />
            <Tarjeta
              titulo={`Utilidad ${rango}`}
              valor={(totales.utilidad >= 0 ? "+" : "−") + money0(Math.abs(totales.utilidad)).replace("−", "")}
              color={totales.utilidad >= 0 ? "text-emerald-700" : "text-red-700"}
              nota="no comparable entre meses"
            />
            <Tarjeta
              titulo="Margen de meses completos"
              valor={totales.margen == null ? "—" : `${totales.margen.toFixed(1)}%`}
              color="text-slate-900"
              nota={
                totales.completos.length
                  ? `${totales.completos.length} meses: ${etiquetaMes(totales.completos[0].mes)}–${etiquetaMes(
                      totales.completos[totales.completos.length - 1].mes,
                    )}`
                  : "aún no hay meses completos"
              }
            />
          </section>

          {/* Gráfica */}
          <section className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-sm font-medium text-slate-700 mb-2">
              Ingresos contra costos por mes
              <span className="text-slate-400 font-normal">
                {" "}· los meses marcados con * no tienen cobertura completa
              </span>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={datosGrafica} margin={{ top: 8, right: 12, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="mes"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: string, i: number) =>
                      datosGrafica[i]?.parcial ? `${v} *` : v
                    }
                  />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={compacto} width={62} />
                  <Tooltip
                    formatter={(v: number, n: string) => [money0(v), n]}
                    labelFormatter={(l: string) => {
                      const d = datosGrafica.find((x) => x.mes === l);
                      return d?.parcial ? `${l} · mes incompleto` : l;
                    }}
                  />
                  <Legend />
                  <Bar dataKey="ingresos" name="Ingresos" fill={COLOR_ING} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="costos" name="Costos" fill={COLOR_COS} radius={[3, 3, 0, 0]} />
                  <Line
                    type="monotone"
                    dataKey="utilidad"
                    name="Utilidad"
                    stroke={COLOR_UTIL}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Estado de resultados */}
          <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2.5 border-b border-slate-200 text-sm font-medium text-slate-700">
              Estado de resultados
              <span className="text-slate-400 font-normal">
                {" "}· pesos mexicanos, {conIva ? "con IVA" : "sin IVA"}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="text-xs w-full border-collapse">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-white text-left font-semibold text-slate-500 uppercase tracking-wide px-3 py-2 border-b border-slate-200 min-w-[13rem]">
                      &nbsp;
                    </th>
                    {meses.map((m) => {
                      const motivo = motivoParcial(m);
                      return (
                        <th
                          key={m.mes}
                          title={motivo ? `Mes incompleto: ${motivo}` : undefined}
                          className={`text-right font-semibold uppercase tracking-wide px-3 py-2 border-b border-slate-200 whitespace-nowrap ${
                            motivo ? "text-amber-700" : "text-slate-500"
                          }`}
                        >
                          {etiquetaMes(m.mes)}
                          <span className="block text-[10px] font-medium normal-case tracking-normal text-slate-400">
                            {motivo ?? " "}
                          </span>
                        </th>
                      );
                    })}
                    <th className="text-right font-semibold text-slate-500 uppercase tracking-wide px-3 py-2 border-b border-l-2 border-slate-300 whitespace-nowrap">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {RENGLONES.map((r, i) => {
                    if (r.tipo === "banda") {
                      return (
                        <tr key={`b${i}`} className="bg-slate-700 text-white">
                          <td className="sticky left-0 z-10 bg-slate-700 px-3 py-1.5 font-semibold uppercase tracking-wider text-[11px]">
                            {r.etiqueta}
                          </td>
                          <td colSpan={meses.length + 1} />
                        </tr>
                      );
                    }
                    const campo = r.campo!;
                    const vals = meses.map((m) => val(m, campo));
                    const suma = vals.reduce((a, b) => a + b, 0);
                    const fuerte = r.tipo !== "linea";
                    return (
                      <tr
                        key={`${r.etiqueta}-${campo}`}
                        className={`border-b border-slate-100 ${
                          fuerte ? "bg-slate-50 font-semibold" : "hover:bg-slate-50"
                        }`}
                      >
                        <td
                          className={`sticky left-0 z-10 px-3 py-1.5 text-left ${
                            fuerte ? "bg-slate-50" : "bg-white"
                          } ${r.tipo === "linea" ? "pl-6 text-slate-600" : "text-slate-800"}`}
                        >
                          {r.etiqueta}
                        </td>
                        {vals.map((v, j) => (
                          <td
                            key={meses[j].mes}
                            className={`px-3 py-1.5 text-right tabular-nums ${
                              r.tipo === "utilidad"
                                ? v >= 0
                                  ? "text-emerald-700"
                                  : "text-red-700"
                                : "text-slate-700"
                            }`}
                          >
                            {v === 0 ? <span className="text-slate-300">—</span> : money2(v)}
                          </td>
                        ))}
                        <td
                          className={`px-3 py-1.5 text-right tabular-nums border-l-2 border-slate-300 font-semibold ${
                            r.tipo === "utilidad"
                              ? suma >= 0
                                ? "text-emerald-700"
                                : "text-red-700"
                              : "text-slate-800"
                          }`}
                        >
                          {money2(suma)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Detalles */}
          <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2.5 border-b border-slate-200 flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Detalle</span>
              {([
                ["ninguno", "Ocultar"],
                ["emitidas", `Emitidas (${q.data.emitidas.length} movimientos)`],
                ["recibidas", `Recibidas (${q.data.recibidas.length} CFDI)`],
              ] as const).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={detalle === v}
                  onClick={() => setDetalle(v)}
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    detalle === v
                      ? "border-slate-300 bg-white text-slate-800"
                      : "border-slate-200 bg-slate-50 text-slate-400"
                  }`}
                >
                  {label}
                </button>
              ))}
              {detalle === "emitidas" && (
                <span className="text-xs text-slate-400">
                  Las de ATRIA aparecen dos veces: el preliminar y su ajuste.
                </span>
              )}
            </div>
            {detalle === "emitidas" && <TablaEmitidas filas={q.data.emitidas} />}
            {detalle === "recibidas" && <TablaRecibidas filas={q.data.recibidas} />}
          </section>

          {/* Cómo leerlo */}
          <section className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
            <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                De dónde sale cada renglón
              </h2>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  <b>Ingresos EE + Potencia y CEL</b> — <code>etrm.factura</code>, por el mes
                  de <code>fecha_limite_pago</code>.
                </li>
                <li>
                  <b>Contratos ATRIA: dos flujos por factura.</b> En modo <code>ajuste</code>,
                  el importe preliminar entra en <code>fecha_limite_pago_preliminar</code> y el
                  ajuste en <code>fecha_limite_pago</code>.
                </li>
                <li>
                  <b>Consultorías</b> — <code>etrm.factura_servicio</code>, misma base de fecha.
                </li>
                <li>
                  <b>Ingresos y costos CENACE</b> — estados de cuenta por mes de{" "}
                  <code>fecha_pago</code>. Ingreso = lo que emite el participante; costo = lo
                  que emite CENACE.
                </li>
                <li>
                  <b>Costos</b> — <code>etrm.factura_recibida</code> por la fecha del CFDI. La
                  línea de <b>CEL</b> se separa leyendo los <b>conceptos</b> del CFDI, no el
                  emisor: Generadora Fénix y FSE facturan energía y certificados.
                </li>
                <li>
                  <b>Proyección B2B</b> — <code>etrm.proyeccion_b2b</code>, descartando los
                  renglones que ya se volvieron factura real para no contarlos dos veces.
                </li>
              </ul>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Cómo leerlo
              </h2>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  <b>Base mixta, a propósito.</b> CENACE va por flujo de pago y los costos del
                  ETRM por la fecha del CFDI, porque las recibidas no tienen fecha de pago
                  confiable.
                </li>
                <li>
                  Los meses marcados en ámbar <b>no son comparables</b>: o no tienen estados de
                  cuenta de CENACE enfrente, o es el mes en curso, o ya sólo traen proyección.
                  El margen sólo usa los meses completos.
                </li>
                <li>
                  Las <b>notas de crédito</b> de proveedor restan de su línea, así que un mes
                  puede salir con costo negativo si la nota supera lo facturado.
                </li>
                <li>
                  Cuentan los CFDI de las tres entidades del grupo: <b>MPP Energy Partners</b>,{" "}
                  <b>PANT3RA</b> y <b>Flamma</b>; el receptor se anota en la columna de nota.
                </li>
                {meta && meta.sin_fecha.length > 0 && (
                  <li>
                    <b>
                      {meta.sin_fecha.length} factura
                      {meta.sin_fecha.length === 1 ? "" : "s"} sin fecha límite capturada
                    </b>{" "}
                    (id {meta.sin_fecha.map((s) => s.id).join(", ")}): se ubica por el cierre de
                    su periodo.
                  </li>
                )}
              </ul>
            </div>
          </section>
        </>
      )}

      {q.data && meses.length === 0 && (
        <div className="text-sm text-slate-500">No hay facturas ni estados de cuenta que mostrar.</div>
      )}
    </div>
  );
}

function Tarjeta({ titulo, valor, nota, color }: {
  titulo: string; valor: string; nota: string; color: string;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{titulo}</div>
      <div className={`text-2xl font-semibold tabular-nums mt-1 ${color}`}>{valor}</div>
      <div className="text-xs text-slate-400 mt-0.5">{nota}</div>
    </div>
  );
}

function TablaEmitidas({ filas }: { filas: PnlEmitida[] }) {
  let mesPrevio = "";
  return (
    <div className="overflow-auto max-h-[30rem]">
      <table className="text-xs w-full border-collapse">
        <thead className="sticky top-0 bg-white z-10">
          <tr className="text-slate-500 uppercase tracking-wide">
            {["Fecha límite", "Mes", "Producto", "Contrato", "Parc.", "Periodo", "Mon",
              "Monto MXN", "Origen"].map((h, i) => (
              <th
                key={h}
                className={`px-3 py-2 border-b border-slate-200 font-semibold whitespace-nowrap ${
                  i === 7 ? "text-right" : "text-left"
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => {
            const mes = f.flp.slice(0, 7);
            const nuevo = mes !== mesPrevio;
            mesPrevio = mes;
            return (
              <tr
                key={`${f.id}-${f.origen}-${f.flp}`}
                className={`hover:bg-slate-50 ${nuevo ? "border-t-2 border-slate-200" : "border-b border-slate-100"}`}
              >
                <td className="px-3 py-1.5 whitespace-nowrap">{f.flp}</td>
                <td className="px-3 py-1.5 font-semibold text-slate-500 whitespace-nowrap">
                  {nuevo ? etiquetaMes(mes) : ""}
                </td>
                <td className="px-3 py-1.5">{f.producto}</td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{f.folio}</td>
                <td className="px-3 py-1.5">{f.n_pago || "—"}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-slate-500">
                  {f.desde} – {f.hasta}
                </td>
                <td className="px-3 py-1.5">
                  <span className={`rounded border px-1.5 text-[10px] font-semibold ${
                    f.moneda === "USD" ? "border-amber-300 text-amber-700" : "border-slate-200 text-slate-500"
                  }`}>
                    {f.moneda}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{money2(f.sin_iva)}</td>
                <td className="px-3 py-1.5 text-slate-400">{f.nota || (f.origen === "definitiva" ? "" : f.origen)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TablaRecibidas({ filas }: { filas: PnlRecibida[] }) {
  let mesPrevio = "";
  return (
    <div className="overflow-auto max-h-[30rem]">
      <table className="text-xs w-full border-collapse">
        <thead className="sticky top-0 bg-white z-10">
          <tr className="text-slate-500 uppercase tracking-wide">
            {["Fecha", "Mes", "Emisor", "Línea del P&L", "Mon", "Monto MXN", "Nota"].map((h, i) => (
              <th
                key={h}
                className={`px-3 py-2 border-b border-slate-200 font-semibold whitespace-nowrap ${
                  i === 5 ? "text-right" : "text-left"
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => {
            const mes = f.fecha.slice(0, 7);
            const nuevo = mes !== mesPrevio;
            mesPrevio = mes;
            return (
              <tr
                key={f.id}
                className={`hover:bg-slate-50 ${nuevo ? "border-t-2 border-slate-200" : "border-b border-slate-100"}`}
              >
                <td className="px-3 py-1.5 whitespace-nowrap">{f.fecha}</td>
                <td className="px-3 py-1.5 font-semibold text-slate-500 whitespace-nowrap">
                  {nuevo ? etiquetaMes(mes) : ""}
                </td>
                <td className="px-3 py-1.5">{f.emisor}</td>
                <td className="px-3 py-1.5 text-slate-600">{LINEA_LABEL[f.linea] ?? f.linea}</td>
                <td className="px-3 py-1.5">
                  <span className={`rounded border px-1.5 text-[10px] font-semibold ${
                    f.moneda !== "MXN" ? "border-amber-300 text-amber-700" : "border-slate-200 text-slate-500"
                  }`}>
                    {f.moneda}
                  </span>
                </td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${f.mxn < 0 ? "text-red-700" : ""}`}>
                  {money2(f.mxn)}
                </td>
                <td className="px-3 py-1.5 text-slate-400">{f.nota}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
